import React, { useEffect, useState } from "react";
import api from "../lib/api";

const RANGES = [
  { id: "7d", label: "7D" },
  { id: "30d", label: "30D" },
  { id: "90d", label: "90D" },
  { id: "1y", label: "1Y" },
];

export default function PriceChart({ endpoint }) {
  const [range, setRange] = useState("90d");
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hover, setHover] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get(endpoint, { params: { range } })
      .then((r) => setPoints(r.data || []))
      .catch(() => setPoints([]))
      .finally(() => setLoading(false));
  }, [endpoint, range]);

  if (loading) {
    return <div className="h-64 flex items-center justify-center text-[var(--text-tertiary)] font-mono text-sm">Loading chart...</div>;
  }
  if (points.length < 2) {
    return <div className="h-64 flex items-center justify-center text-[var(--text-tertiary)] font-mono text-sm">Not enough data for this range.</div>;
  }

  const values = points.map((p) => p.close);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range_ = max - min || 1;
  const w = 900, h = 260, pad = 10;

  const path = points.map((p, i) => {
    const x = pad + (i / (points.length - 1)) * (w - pad * 2);
    const y = pad + (1 - (p.close - min) / range_) * (h - pad * 2);
    return `${i === 0 ? "M" : "L"} ${x} ${y}`;
  }).join(" ");

  const isUp = values[values.length - 1] >= values[0];
  const color = isUp ? "#4ade80" : "#f87171";

  const areaPath = path + ` L ${w - pad} ${h - pad} L ${pad} ${h - pad} Z`;

  const handleMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const idx = Math.round((x / rect.width) * (points.length - 1));
    if (points[idx]) setHover({ ...points[idx], idx });
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        {RANGES.map((r) => (
          <button key={r.id} onClick={() => setRange(r.id)}
            className={"px-3 py-1.5 text-xs font-mono uppercase tracking-widest border rounded-full transition " +
              (range === r.id ? "bg-yellow-400 text-black border-yellow-400" : "border-[var(--border-soft)] text-[var(--text-tertiary)] hover:border-yellow-400 hover:text-yellow-400")}>
            {r.label}
          </button>
        ))}
      </div>

      <div className="relative">
        <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-64" onMouseMove={handleMove} onMouseLeave={() => setHover(null)}>
          <defs>
            <linearGradient id="chartFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity="0.25" />
              <stop offset="100%" stopColor={color} stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d={areaPath} fill="url(#chartFill)" />
          <path d={path} fill="none" stroke={color} strokeWidth="2" />
          {hover && (
            <line x1={pad + (hover.idx / (points.length - 1)) * (w - pad * 2)} y1={pad} x2={pad + (hover.idx / (points.length - 1)) * (w - pad * 2)} y2={h - pad} stroke="var(--border-soft)" strokeWidth="1" />
          )}
        </svg>
        {hover && (
          <div className="absolute top-0 left-0 bg-[var(--bg-elevated)] border border-[var(--border-soft)] rounded-lg px-3 py-2 text-xs font-mono pointer-events-none">
            <div className="text-[var(--text-tertiary)]">{hover.date}</div>
            <div className="text-[var(--text-primary)] font-bold">{hover.close.toLocaleString(undefined, { maximumFractionDigits: 5 })}</div>
          </div>
        )}
      </div>
    </div>
  );
}